"use client";

import { Loader, ShieldCheck, Clock, Lock, Star } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function CtaForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="mx-auto max-w-xl rounded-md border border-border bg-card/60 backdrop-blur-sm p-6 text-center">
        <p className="font-default text-lg text-foreground">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-4 rounded-md border-primary text-primary hover:bg-primary hover:text-primary-foreground">
          Request another quote
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-4">
      <div className="mx-auto max-w-xl rounded-md border border-border bg-card/60 backdrop-blur-sm p-2 shadow-2xl">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
          <FormField control={control} name="email" rules={{
          required: "Email is required",
          pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: "Please enter a valid email"
          }
        }} render={({
          field
        }) => <FormItem className="flex-1">
                <FormControl>
                  <Input type="email" placeholder="Enter your email address" className="h-11 sm:h-12 min-h-[44px] border-0 bg-transparent px-4 text-base text-foreground placeholder:text-muted-foreground focus-visible:ring-0 focus-visible:border-0 font-default" {...field} />
                </FormControl>
                <FormMessage className="px-4 font-default" />
              </FormItem>} />

          <Button type="submit" disabled={isSubmitting} size="lg" className="h-12 px-6 bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg shadow-primary/20 font-default font-bold uppercase tracking-widest rounded-md">
            {isSubmitting ? <>
                <Loader className="mr-2 size-4 animate-spin" />
                Processing...
              </> : "Get Free Quote"}
          </Button>
        </div>
      </div>

      {submitError && <div className="mx-auto max-w-xl rounded-md border border-destructive/40 bg-destructive/10 p-3 text-center">
          <p className="text-sm text-destructive-foreground font-default">{submitError}</p>
        </div>}
    </Form>;
}
export default function SecureYourFreeInspection() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const trustMarks = [{
    id: 1,
    label: "Licensed & Insured",
    stars: true
  }, {
    id: 2,
    label: "Family Owned",
    stars: true
  }, {
    id: 3,
    label: "Satisfaction Guaranteed",
    stars: false
  }];
  const reassurances = [{
    id: 1,
    icon: Clock,
    text: "Free inspections"
  }, {
    id: 2,
    icon: ShieldCheck,
    text: "Eco-friendly treatments"
  }, {
    id: 3,
    icon: Lock,
    text: "Secure home perimeter"
  }];
  return <section data-section-id="70" id="secure-your-free-inspection" className="dark bg-background text-foreground relative overflow-hidden py-16 sm:py-24 md:py-[180px] px-4">
      {}
      <div className="pointer-events-none absolute inset-0 opacity-[0.03]" style={{
      backgroundImage: "radial-gradient(circle, hsl(45 50% 55%) 1px, transparent 1px)",
      backgroundSize: "32px 32px"
    }} aria-hidden="true" />

      {}
      <div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[900px] max-w-[120vw] max-h-[120vw] rounded-md" style={{
      background: "radial-gradient(circle at center, hsla(45, 50%, 55%, 0.15) 0%, hsla(220, 15%, 6%, 0.1) 40%, transparent 70%)",
      filter: "blur(60px)"
    }} aria-hidden="true" />

      <div ref={ref} className={`relative z-10 mx-auto max-w-[720px] text-center transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        {}
        <div className="inline-flex items-center gap-2 rounded-md border border-border bg-card/40 backdrop-blur-sm px-4 py-1.5">
          <span className="size-1.5 rounded-md bg-primary" />
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-primary">
            Elite Protection · Free Inspections
          </span>
        </div>

        {}
        <h2 className="font-default mt-8 font-semibold tracking-tight text-foreground" style={{
        fontSize: "clamp(36px, 5vw, 72px)",
        lineHeight: 1.05
      }}>
          Secure your home's sanctuary{" "}
          <span className="bg-clip-text text-transparent italic font-serif" style={{
          backgroundImage: "linear-gradient(135deg, hsl(45 50% 55%), hsl(45 60% 85%), hsl(45 50% 55%))"
        }}>
            defense
          </span>
          .
        </h2>

        {}
        <p className="mt-6 text-lg text-muted-foreground leading-relaxed font-default">
          Join the Triangle homeowners who trust Attic to Basement 
          to help keep pests out and protect their homes.
        </p>

        {}
        <div className="mt-12">
          <FormProvider formKey="free_inspection_form_home" formId="free_inspection_form_home" sectionName="ready-to-sync" defaultValues={{
          full_name: "",
          email: "",
          team_size: "",
          message: "",
          agree: false
        }} validationRules={{
          email: {
            required: true,
            pattern: "email"
          }
        }} submitText="Get Free Quote" successMessage="Request received. Our technicians will contact you shortly to schedule your inspection." errorMessage="Something went wrong. Please call us at 919-896-1953.">
            <CtaForm />
          </FormProvider>
        </div>

        {}
        <div className="mt-5 flex flex-wrap items-center justify-center gap-x-5 gap-y-2">
          {reassurances.map((item, i) => {
          const Icon = item.icon;
          return <div key={item.id} data-index={i} className="flex items-center gap-1.5 text-xs text-muted-foreground font-default">
                <Icon className="size-3.5 text-primary" />
                <span>{item.text}</span>
              </div>;
        })}
        </div>

        {}
        <div className="mt-20 flex flex-wrap items-center justify-center gap-x-10 gap-y-4 border-t border-border pt-10">
          {trustMarks.map((mark, i) => <div key={mark.id} data-index={i} className="flex items-center gap-2 text-sm text-muted-foreground">
              {mark.stars && <div className="flex items-center gap-0.5">
                  {[0, 1, 2, 3, 4].map(s => <Star key={s} className="size-3 fill-primary text-primary" />)}
                </div>}
              {!mark.stars && <ShieldCheck className="size-4 text-primary" />}
              <span className="font-mono text-xs uppercase tracking-wider">
                {mark.label}
              </span>
            </div>)}
        </div>
      </div>
    </section>;
}